import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { FormControl } from '@angular/forms';
import { slideInOut } from '../../animations/slide-in-out.animation';
import { opacityInOut } from '../../animations/opacity-in-out.animation';
import { getWindow } from 'ssr-window';

const CHUNCK_SIZE = 10;

@Component({
  selector: 'a-select-autocomplete',
  templateUrl: 'a-select-autocomplete.component.html',
  styleUrls: ['a-select-autocomplete.component.scss'],
  animations: [slideInOut, opacityInOut]
})
export class ASelectAutocompeteComponent implements OnInit, OnDestroy {

  // TODO Adjust component for ['string', 'string'] arrays
  // TODO Adjust component for non isMultiple

  @ViewChild('scrolledEl') scrolledEl: ElementRef | undefined;
  @ViewChild('selectEl') selectEl: ElementRef | undefined;
  @Input() label = '';
  @Input() valueKey = 'id';
  @Input() labelKey = 'label';
  @Input() grayBg = true;
  @Input() method = null;
  @Input() items = [];
  @Input() isSubmitted = true;
  @Input() isOptional = false;
  @Input() isDisabled = false;
  @Input() isMultiple = true;
  @Input() control: FormControl = new FormControl();
  @Input() startSearchLength = 0;
  @Input() bindIds = false;
  @Output() onBlur = new EventEmitter<any>();
  
  filterParams$ = new BehaviorSubject<any>({ 
    toPush: false,
    filters: { query: '', take: CHUNCK_SIZE, skip: 0 }
  });
  currentPage = 0;
  searchValue = ''; /// ???
  itemsList = [];
  selectedItem;
  loadingResults = false;
  initialLoading = false;
  showItems = false;
  inputFocused = false;
  openPositionTop = false;
  private allItemsList = [];
  private destroy$ = new Subject<boolean>();

  constructor() {
  }

  ngOnInit(): void {
    if (this.control.value) {
      this.selectedItem = this.control.value;
      this.itemsList = this.control.value;
    }
    
    if (this.method) {
      this.initialLoading = true;
      this.getResults().pipe(take(1)).subscribe(res => {
        this.initialLoading = false;
        if (res && res.length) {
          this.itemsList = this.allItemsList = this.mergeArrays(this.itemsList, res);
          this.hydrateSelectionFromControl();
        }
      });
      this.initAutocomplete();
    }

    if (this.items) {
      this.itemsList = this.allItemsList = this.items;
      this.hydrateSelectionFromControl();
      this.initSearch();
    }
  }

  private initSearch() {
    this.filterParams$.pipe(
      takeUntil(this.destroy$),
      map(params => {
        const query = params.filters.query;
        if (query && query.trim().length > this.startSearchLength) {
          this.itemsList = this.allItemsList.filter(item => 
            item[this.labelKey].toLowerCase().trim().includes(query.toLowerCase().trim())
          )
        } else {
          this.itemsList = this.allItemsList;
        }
      })
    ).subscribe();
  }

  private initAutocomplete() {
    this.filterParams$.pipe(
      takeUntil(this.destroy$),
      map(params => {
        if (!params.toPush) {
          const query = params.filters.query;
          if (query && query.trim().length > this.startSearchLength) {
            this.itemsList = this.allItemsList.filter(item => 
              item[this.labelKey].toLowerCase().trim().includes(query.toLowerCase().trim())
            )
          } else {
            this.itemsList = this.allItemsList;
          }
        }
        this.loadingResults = true;
        return params;
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(params => {
        const query = params.filters.query;
        if ((query && query.trim().length > this.startSearchLength) || params.toPush) {
          return this.getResults();
        } else {
          this.loadingResults = false;
          return of([]);
        }
      })
    ).subscribe(res => {
      // console.log('GET VACANCY', res);
      this.itemsList = this.mergeArrays(this.itemsList, res);
       this.hydrateSelectionFromControl();
    });
  }

  private getResults(): Observable<any> {
    this.loadingResults = true;
    const params = this.filterParams$.getValue().filters;
    return this.method(params).pipe(map(res => {
      this.loadingResults = false;
      return res ? res : [];
    }));
  }

  onScrollDown(e) {
    if (this.currentPage !== null && e && e.currentScrollPosition && e.currentScrollPosition > 100) {
      this.currentPage = this.currentPage + 1;
      const currentParams = this.filterParams$.value.filters;
      this.filterParams$.next({ toPush: true, filters: { ...currentParams, skip: this.currentPage * CHUNCK_SIZE }});
    }
  }

  onFocus() {
    this.showDropdown();
  }

  onBlurAction() {
    if (this.isMultiple) {
      // this.selectEl.nativeElement.value = null;
      // this.findResult('');
    } else {
      this.searchValue = this.selectedItem ? this.selectedItem[this.labelKey] : '';
    }
  }

  showDropdown(val: boolean = true) {
    this.showItems = val;
    if (this.selectEl) {
      const position = this.selectEl.nativeElement.getBoundingClientRect().top;
      this.openPositionTop = getWindow().innerHeight - position < 240;
    }
  }

  selectItem(val: any) {
    if (val) {
      if (this.isMultiple) {
        const items = Array.isArray(this.selectedItem) && this.selectedItem.length ? this.selectedItem : []
        const foundIndex = items.findIndex(v => v[this.valueKey] === val[this.valueKey]);
        foundIndex === -1 ? items.push(val) : items.splice(foundIndex, 1);
        this.selectedItem = items.length ? items : null;
        this.searchValue = null;
      } else {
        this.selectedItem = val;
        this.searchValue = val[this.labelKey];
      }
      this.writeControl();
      this.onBlur.emit();
      this.showDropdown(false);
    }
  }

  removeSelected(val) {
    const items = this.selectedItem.filter(v => v[this.valueKey] !== val[this.valueKey]);
    this.selectedItem = items.length ? items : null;
    this.writeControl();
  }

  findResult(query: any) {
    this.searchValue = query;
    this.currentPage = 0;
    this.filterParams$.next({
      toPush: false,
      filters: { query, take: CHUNCK_SIZE, skip: 0 }
    })
  }

  checkIfActive(val) {
    return this.selectedItem.some(item => item[this.valueKey] === val[this.valueKey]);
  }

  clear() {
    this.selectEl.nativeElement.value = null;
    this.filterParams$.next({
      toPush: false,
      filters: { query: '', take: CHUNCK_SIZE, skip: 0 }
    });
  }

  private mergeArrays(arr1, arr2) {
    const mergedArray = [...arr1, ...arr2];
    const uniqueArray = Array.from(new Set(mergedArray.map(item => item[this.valueKey])))
      .map(id => mergedArray.find(item => item[this.valueKey] === id));
    return uniqueArray;
    return uniqueArray.sort((a, b) => a[this.labelKey].localeCompare(b[this.labelKey]));
  }

  private writeControl() {
    if (this.bindIds) {
      const val = this.isMultiple
        ? (this.selectedItem ? this.selectedItem.map((o: any) => o?.[this.valueKey]) : [])
        : (this.selectedItem ? this.selectedItem[this.valueKey] : null);
      this.control.setValue(val);
    } else {
      this.control.setValue(this.selectedItem);
    }
}

private hydrateSelectionFromControl() {
  // If control already has a value (IDs when bindIds=true), resolve to objects for the UI
  const cv = this.control?.value;
  if (!this.bindIds || cv == null) {
    // old flow (object binding)
    if (cv) {
      this.selectedItem = cv;
      if (!this.isMultiple) this.searchValue = this.selectedItem?.[this.labelKey] ?? '';
    }
    return;
  }

  // bindIds = true
  const list = this.allItemsList?.length ? this.allItemsList : this.itemsList || [];
  if (this.isMultiple) {
    const ids: string[] = Array.isArray(cv) ? cv : [];
    this.selectedItem = ids
      .map(id => list.find((it: any) => it?.[this.valueKey] === id))
      .filter(Boolean);
  } else {
    const id: string = cv;
    this.selectedItem = list.find((it: any) => it?.[this.valueKey] === id) || null;
    this.searchValue = this.selectedItem ? this.selectedItem[this.labelKey] : '';
  }
}

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
